import { format, parse } from 'date-fns';
import React from 'react';
import { FormRenderProps } from 'react-final-form';
import * as Yup from 'yup';
import { Button } from '@economic/taco';

import { Field, Form } from '../../shared/components/Form';
import { t } from '../../shared/components/l10n/l10n';
import date from '../../shared/util/utilities/date';
import { ProgressDots } from '../../shared/components/ui/progressDots/ProgressDots';
import { SubmitErrors } from './SubmitErrors';
import { OnboardingCreateAccountingYear, OnboardingStatusAccountingYears } from './types';
import internalApiClient from '../../shared/util/api/internalApiClient';

const onboardingCreateAccountingYear = (data: OnboardingCreateAccountingYear) =>
    internalApiClient.post(`/onboarding/accounting-year`, data);

const validationSchema = Yup.object().shape({
    accountingYearType: Yup.number().required(),
});

function formatDate(d: string) {
    return date.format(date.parseFromISOString(d));
}
function formatISODate(d: string) {
    return format(parse(d), 'YYYY-MM-DD');
}

interface Props {
    data: OnboardingStatusAccountingYears;
    onBack: () => void;
    onDone: () => void;
    stepCount: number;
}

export default function StepAccountingYear({ data, onBack, onDone, stepCount }: Props) {
    async function saveAccountingYear(values: { accountingYearType: number; from: string; to: string }) {
        const from = formatISODate(
            values.accountingYearType === 0
                ? data.currentAccountingYear[0]
                : values.accountingYearType === 1
                ? data.shiftedAccountingYear[0]
                : values.from
        );
        const to = formatISODate(
            values.accountingYearType === 0
                ? data.currentAccountingYear[1]
                : values.accountingYearType === 1
                ? data.shiftedAccountingYear[1]
                : values.to
        );

        try {
            await onboardingCreateAccountingYear({ from, to });
            onDone();
        } catch (error) {
            if (error && error.response && error.response.data && error.response.data.message) {
                return Promise.reject({
                    validation: {
                        '': error.response.data.message,
                    },
                });
            }

            throw error;
        }
    }

    return (
        <div className="onboarding__container__fixed-size">
            <div className="onboarding onboarding__accounting-year text-center">
                <ProgressDots stepsCount={stepCount} currentStep={3} />

                <h1>{t`ONBOARDING_GETTINGSTARTED_ACCOUNTINGYEAR_TITLE`}</h1>

                <p>{t`ONBOARDING_GETTINGSTARTED_ACCOUNTINGYEAR_CONTENT`}</p>

                {data.hasAccountingYears ? (
                    <>
                        <strong>{t`GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED`}</strong>

                        <div className="selected-accounting-year">
                            <span className="from">{formatDate(data.selectedAccountingYear.from)}</span>
                            <span className="to">{formatDate(data.selectedAccountingYear.to)}</span>
                        </div>

                        <p>
                            <span>
                                {t`GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_I`} <br />
                                {t`GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_I`}
                                <i className="long-arrow-right" />
                                {t`GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_II`}
                                <i className="long-arrow-right" />
                                {t`GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_III`}
                            </span>
                        </p>

                        <Form onSubmit={onDone} skipNotifications>
                            <div className="onboarding__navigation-buttons">
                                <Button appearance="primary" type="submit">{t`ONBOARDING_GETTINGSTARTED_NEXT`}</Button>
                                <Button
                                    appearance="discrete"
                                    onClick={e => {
                                        e.preventDefault();
                                        onBack();
                                    }}>{t`ONBOARDING_GETTINGSTARTED_BACK`}</Button>
                            </div>
                        </Form>
                    </>
                ) : (
                    <Form
                        skipNotifications
                        onSubmit={saveAccountingYear}
                        validationSchema={validationSchema}
                        validateOnBlur={false}
                        initialData={{
                            from: data.currentAccountingYear[0],
                            to: data.currentAccountingYear[1],
                        }}>
                        {({ submitErrors }: FormRenderProps) => (
                            <>
                                <div className="onboarding__form-content">
                                    <Field.NumberRadioGroup
                                        name="accountingYearType"
                                        options={[
                                            {
                                                text: `${t('GETTING_STARTED_ACCOUNTING_YEAR_OPTION_CURRENT')} (${formatDate(
                                                    data.currentAccountingYear[0]
                                                )} - ${formatDate(data.currentAccountingYear[1])})`,
                                                value: 0,
                                            },
                                            {
                                                text: `${t('GETTING_STARTED_ACCOUNTING_YEAR_OPTION_SHIFTED')} (${formatDate(
                                                    data.shiftedAccountingYear[0]
                                                )} - ${formatDate(data.shiftedAccountingYear[1])})`,
                                                value: 1,
                                            },
                                            {
                                                text: (
                                                    <span className="manual-accounting-year flex flex-row items-center">
                                                        <span className="flex shrink-0 mr-2">{t`GETTING_STARTED_ACCOUNTING_YEAR_OPTION_CUSTOM`}</span>
                                                        <Field.DatePicker name="from" fieldClassName="mr-1" />
                                                        {'-'}
                                                        <Field.DatePicker name="to" fieldClassName="ml-1" />
                                                    </span>
                                                ),
                                                value: 2,
                                            },
                                        ]}
                                    />

                                    <SubmitErrors errors={submitErrors} />
                                </div>

                                <div className="onboarding__navigation-buttons">
                                    <Button appearance="primary" type="submit">{t`ONBOARDING_GETTINGSTARTED_NEXT`}</Button>
                                    <Button
                                        appearance="discrete"
                                        onClick={e => {
                                            e.preventDefault();
                                            onBack();
                                        }}>{t`ONBOARDING_GETTINGSTARTED_BACK`}</Button>
                                </div>
                            </>
                        )}
                    </Form>
                )}
            </div>
        </div>
    );
}
